import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { mediaUrl } from "../../lib/api";
import { X, ArrowRight, FileDown, Maximize2 } from "lucide-react";

export default function ProductModal({ product, onClose, onEnquire }) {
  return (
    <AnimatePresence>
      {product && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose} data-testid="product-modal"
          className="fixed inset-0 z-[60] grid place-items-center p-4 sm:p-8 bg-ink/85 backdrop-blur-sm overflow-y-auto"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-4xl bg-card border border-white/10 grid md:grid-cols-2"
          >
            <button onClick={onClose} data-testid="modal-close"
              className="absolute top-4 right-4 z-10 w-10 h-10 grid place-items-center bg-ink/80 border border-white/10 text-white hover:text-signal hover:border-signal transition-colors">
              <X className="w-5 h-5" />
            </button>

            <div className="relative aspect-square md:aspect-auto bg-ink overflow-hidden">
              <img src={mediaUrl(product.image_url)} alt={product.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-card/60 to-transparent md:bg-gradient-to-r" />
            </div>

            <div className="p-8 lg:p-10">
              <span className="font-mono text-[10px] uppercase tracking-widest bg-signal text-ink px-2 py-1">{product.brand}</span>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-5 mb-2">{product.category}</div>
              <h3 className="font-head font-black uppercase text-3xl text-white leading-tight mb-4">{product.name}</h3>
              <p className="text-slate-400 leading-relaxed mb-6">{product.description}</p>

              <div className="border-t border-white/10 pt-5 mb-6">
                <div className="font-mono text-[10px] uppercase tracking-widest text-signal mb-3">Specifications</div>
                <dl className="space-y-2">
                  {Object.entries(product.specs || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 border-b border-white/5 pb-2">
                      <dt className="font-mono text-xs text-slate-500 uppercase">{k}</dt>
                      <dd className="font-mono text-sm text-white text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <button onClick={onEnquire} data-testid="modal-enquire-btn"
                className="group w-full font-head font-bold uppercase tracking-wider px-6 py-4 bg-signal text-ink hover:bg-signal-hover transition-colors flex items-center justify-center gap-2">
                Request Datasheet & Quote
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <Link to={`/product/${product.slug}`} data-testid="modal-details-link"
                  className="font-mono text-[11px] uppercase tracking-wider px-4 py-3 border border-white/15 text-slate-300 hover:border-signal hover:text-signal transition-colors flex items-center justify-center gap-2">
                  <Maximize2 className="w-4 h-4" /> Full Details
                </Link>
                {product.datasheet_url ? (
                  <a href={mediaUrl(product.datasheet_url)} target="_blank" rel="noopener noreferrer" data-testid="modal-datasheet"
                    className="font-mono text-[11px] uppercase tracking-wider px-4 py-3 border border-white/15 text-slate-300 hover:border-signal hover:text-signal transition-colors flex items-center justify-center gap-2">
                    <FileDown className="w-4 h-4" /> Datasheet
                  </a>
                ) : (
                  <span className="font-mono text-[11px] uppercase tracking-wider px-4 py-3 border border-white/5 text-slate-600 flex items-center justify-center gap-2 cursor-not-allowed">
                    <FileDown className="w-4 h-4" /> No PDF
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
